import { useCallback, useEffect, useState } from 'react'

export interface User {
  readonly id: string
  readonly name: string
  readonly avatar: string | null
}

export interface Session {
  readonly user: User | null
  /** False when the deployment has no Google credentials configured. */
  readonly canSignIn: boolean
  /** The dev-only mock identity provider is available. */
  readonly dev: boolean
  readonly loading: boolean
}

const UNKNOWN: Session = { user: null, canSignIn: false, dev: false, loading: true }

/**
 * Who the browser is, if anyone.
 *
 * Loading is *not* a gate: every screen renders immediately in its guest form
 * and the account details fill in when they arrive. Blocking the page on this
 * request would mean a signed-out visitor — the common case — waits on a round
 * trip that was only ever going to say "nobody".
 */
export const useSession = (): Session & { readonly refresh: () => void } => {
  const [session, setSession] = useState<Session>(UNKNOWN)

  const refresh = useCallback((): void => {
    void (async (): Promise<void> => {
      try {
        const response = await fetch('/api/auth/me', { credentials: 'same-origin' })
        const body = (await response.json()) as Omit<Session, 'loading'>
        setSession({ ...body, loading: false })
      } catch {
        // Offline, or the API is down. A guest with no sign-in button is a
        // perfectly usable chess site, so this is not worth surfacing.
        setSession({ user: null, canSignIn: false, dev: false, loading: false })
      }
    })()
  }, [])

  useEffect(refresh, [refresh])

  return { ...session, refresh }
}

/** Where to send the browser to sign in, returning to where it started. */
export const signInHref = (): string =>
  `/api/auth/google?next=${encodeURIComponent(window.location.pathname + window.location.hash)}`
